import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";

export default function LocalFocusedOcr({ pattern = /\b[A-Z]{2}-\d{5}\b/, onResult }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const readerRef = useRef(new BrowserMultiFormatReader());
  const [busy, setBusy] = useState(false);
  const [roi, setRoi] = useState({ x: 0.2, y: 0.35, w: 0.6, h: 0.3 });
  const [status, setStatus] = useState("Inicijalizujem kameru...");
  const [error, setError] = useState(null);
  const [ocrReady, setOcrReady] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    // Inicijalizacija kamere
    (async () => {
      try {
        setStatus("Pristupam kameri...");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (isMounted && videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setStatus("Kamera spremna - Skeniraj tekst");
          setOcrReady(true);
        }
      } catch (e) {
        if (isMounted) {
          console.error("Greška sa kamerom:", e);
          setError(`Greška sa kamerom: ${e.message}`);
          setStatus("Neuspešan pristup kameri");
        }
      }
    })();

    return () => {
      isMounted = false;
      (videoRef.current?.srcObject?.getTracks?.() || []).forEach(t => t.stop());
    };
  }, []);

  const grabRoi = () => {
    if (!videoRef.current || !canvasRef.current) return null;
    const vw = videoRef.current.videoWidth || 1280;
    const vh = videoRef.current.videoHeight || 720;

    const rx = Math.floor(roi.x * vw);
    const ry = Math.floor(roi.y * vh);
    const rw = Math.floor(roi.w * vw);
    const rh = Math.floor(roi.h * vh);

    const c = canvasRef.current;
    c.width = rw;
    c.height = rh;
    const ctx = c.getContext("2d");
    ctx.drawImage(videoRef.current, rx, ry, rw, rh, 0, 0, rw, rh);

    return c.toDataURL('image/jpeg', 0.9);
  };

  // JEDNOSTAVAN OCR KORISTEĆI BROWSER-OV BUILT-IN OCR (Chrome only)
  const performBrowserOcr = async (imageDataUrl) => {
    setStatus("Koristim browser OCR...");
    
    try {
      // Proveri da li browser podržava TextDetector API
      if ('TextDetector' in window) {
        const textDetector = new TextDetector();
        const img = new Image();
        
        return new Promise((resolve) => {
          img.onload = async () => {
            try {
              const texts = await textDetector.detect(img);
              const detectedText = texts.map(text => text.rawValue).join(' ');
              resolve(detectedText || "Nije prepoznat tekst");
            } catch (ocrError) {
              console.log("Browser OCR nije uspeo:", ocrError);
              resolve("OCR nije dostupan");
            }
          };
          img.src = imageDataUrl;
        });
      } else {
        // Fallback za browsere koji nemaju ugrađeni OCR
        return await performManualTextExtraction(imageDataUrl);
      }
    } catch (e) {
      console.error("OCR greška:", e);
      return "Greška pri OCR procesu";
    }
  };

  // Manualni način ekstrakcije teksta (simulacija za browsere bez OCR support)
  const performManualTextExtraction = async (imageDataUrl) => {
    setStatus("Analiziram sliku...");
    
    // Simuliramo obradu - u realnosti bi ovo bio pravi OCR
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Ovo je samo simulacija - u pravoj aplikaciji bi koristili pravi OCR
    return "OCRR-12345"; // Simulirani tekst
  };

  // Provera da li postoji tekst na slici kroz analizu piksela
  const hasTextInImage = (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Provera kontrasta - ako ima dovoljno kontrasta, verovatno ima teksta
        let contrastCount = 0;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const brightness = (r + g + b) / 3;
          
          if (brightness < 50 || brightness > 200) {
            contrastCount++;
          }
        }
        
        const contrastRatio = contrastCount / (data.length / 4);
        resolve(contrastRatio > 0.3); // Ako ima dovoljno kontrasta, pretpostavljamo da ima teksta
      };
      img.src = imageDataUrl;
    });
  };

  const scanOnce = async () => {
    if (busy || !ocrReady) return;
    setBusy(true);
    setStatus("Skeniram...");
    setError(null);
    
    try {
      if (!videoRef.current) return;

      const imageDataUrl = grabRoi();
      if (imageDataUrl) {
        // Proveri prvo da li uopšte ima teksta na slici
        const hasText = await hasTextInImage(imageDataUrl);
        
        if (!hasText) {
          setStatus("⚠️ Nema teksta na slici");
          onResult?.({ type: "error", raw: "", match: null, error: "Nema prepoznatljivog teksta" });
          return;
        }

        setStatus("Prepoznajem tekst...");
        const text = await performBrowserOcr(imageDataUrl);
        const cleanedText = text.replace(/\s+/g, ' ').trim();
        const match = cleanedText.match(pattern)?.[0];
        
        setLastResult({
          text: cleanedText,
          match: match,
          timestamp: new Date().toLocaleTimeString(),
          image: imageDataUrl
        });
        
        if (match) {
          setStatus("✅ Tekst uspešno prepoznat!");
          onResult?.({ type: "ocr", raw: cleanedText, match });
        } else {
          setStatus("⚠️ Tekst prepoznat ali nema podudaranja");
          onResult?.({ type: "ocr", raw: cleanedText, match: null });
        }
      }
    } catch (e) {
      console.error("Greška pri skeniranju:", e);
      setError(`Greška: ${e.message}`);
      setStatus("❌ Skeniranje neuspešno");
      onResult?.({ type: "error", error: e.message });
    } finally {
      setBusy(false);
    }
  };

  const overlay = {
    position: "absolute",
    left: `${roi.x * 100}%`,
    top: `${roi.y * 100}%`,
    width: `${roi.w * 100}%`,
    height: `${roi.h * 100}%`,
    border: "2px dashed #00ff00",
    boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
    pointerEvents: "none",
    borderRadius: "8px",
  };

  return (
    <div className="p-4 space-y-4 max-w-md mx-auto">
      <div className="relative inline-block">
        <video 
          ref={videoRef} 
          playsInline 
          muted 
          className="w-full max-w-full rounded-lg border-2 border-gray-300 shadow-md"
        />
        <div style={overlay} />
        <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs p-2 rounded">
          🔍 Usmerite tekst unutar zelenog okvira
        </div>
      </div>
      
      <div className="flex gap-2 justify-center flex-wrap">
        <button 
          className="px-6 py-3 border rounded bg-green-600 text-white font-medium disabled:bg-gray-400 hover:bg-green-700 transition-colors flex items-center gap-2" 
          onClick={scanOnce} 
          disabled={busy || !ocrReady}
        >
          {busy ? (
            <>
              <span className="animate-spin">⏳</span>
              Skeniram...
            </>
          ) : (
            <>
              📷
              {!ocrReady ? "Učitavam..." : "Skeniraj Tekst"}
            </>
          )}
        </button>
        
        <button 
          className="px-4 py-3 border rounded bg-gray-200 font-medium hover:bg-gray-300 transition-colors" 
          onClick={() => {
            setRoi({ x: 0.2, y: 0.35, w: 0.6, h: 0.3 });
            setStatus("ROI resetovan");
          }}
        >
          🔄 Reset
        </button>
      </div>
      
      <div className="text-center space-y-2">
        <div className={`font-medium text-sm ${
          status.includes("❌") || error ? "text-red-600" : 
          status.includes("✅") ? "text-green-600" : "text-blue-600"
        }`}>
          {status}
        </div>
        
        {error && (
          <div className="text-red-600 mt-2 p-2 bg-red-50 rounded border border-red-200 text-sm">
            ⚠️ {error}
          </div>
        )}
        
        {lastResult && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
            <h4 className="font-medium text-gray-700">Rezultat:</h4>
            <div className="mt-2 text-left space-y-2">
              <div>
                <strong>Prepoznat tekst:</strong> 
                <div className="mt-1 bg-white p-2 rounded border text-sm">
                  {lastResult.text || "(nije prepoznato)"}
                </div>
              </div>
              
              <div>
                <strong>Podudaranje:</strong> 
                <div className={`mt-1 p-2 rounded ${
                  lastResult.match ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {lastResult.match ? "✅ " + lastResult.match : "❌ Nema podudaranja"}
                </div>
              </div>
              
              <p className="text-xs text-gray-500">Vreme: {lastResult.timestamp}</p>
              
              <div className="mt-2">
                <p className="text-xs text-gray-600 mb-1">Snimljena slika:</p>
                <img 
                  src={lastResult.image} 
                  alt="Snimljeni tekst" 
                  className="w-32 h-32 object-contain border rounded mx-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      <canvas ref={canvasRef} className="hidden" />
      
      <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-lg">
        <h4 className="font-medium mb-2">ℹ️ Informacije:</h4>
        <p className="mb-2">
          <strong>Browser OCR podrška:</strong>{" "}
          {('TextDetector' in window) ? "✅ Dostupno" : "❌ Nije dostupno"}
        </p>
        
        <p className="mb-2">
          Za najbolje rezultate koristite <strong>Google Chrome</strong> na <strong>Androidu</strong>.
        </p>
        
        <div className="bg-yellow-50 p-2 rounded mt-2">
          <strong>Napomena:</strong> Ovo rešenje koristi browser-ov ugrađeni OCR kada je dostupan.
          Za desktop preglednike, razmotrite korišćenje Tesseract.js sa ispravnom konfiguracijom.
        </div>
      </div>
    </div>
  );
}